import type { CreateProductRequest, UpdateProductRequest } from '@conai/shared-types';
import type { ProductListItem, ProductFormData, ProductFormErrors, ListEnvelope } from './types';
import { formFieldOrder, emptyProductForm, restrictionTriggerFields } from './types';
import type { ProductItem } from './components';

// ── primitive helpers ──────────────────────────────────────────────────────────

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

// ── list helpers ───────────────────────────────────────────────────────────────

export function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const data = payload as ListEnvelope<T> | undefined;
  if (!data) return [];
  if (Array.isArray(data.data)) return data.data;
  if (data.data && typeof data.data === 'object') {
    return data.data.data ?? data.data.results ?? data.data.items ?? data.data.products ?? [];
  }
  return data.results ?? data.items ?? data.products ?? [];
}

export function extractCount<T>(payload: unknown, fallback: number): number {
  if (Array.isArray(payload)) return payload.length;
  const data = payload as ListEnvelope<T> | undefined;
  return data?.count ?? data?.total ?? data?.meta?.count ?? fallback;
}

// ── product field helpers ──────────────────────────────────────────────────────

export function toPriceNumber(product: ProductListItem): number {
  // API uses "price" for selling price in list/detail responses
  const raw = product.price ?? product.base_price;
  const v = Number.parseFloat(String(raw ?? '0'));
  return Number.isFinite(v) ? v : 0;
}

export function getCategoryName(product: ProductListItem): string {
  // API returns category_name as a flat string field
  if (product.category_name) return product.category_name;
  return 'Uncategorized';
}

export function getSupplierName(product: ProductListItem): string {
  // API returns supplier_name as a flat string field
  return product.supplier_name ?? '-';
}

export function getCategoryId(product: Partial<ProductListItem>): string {
  // API returns category as UUID string directly (not nested object)
  if (typeof product.category === 'string' && product.category) return product.category;
  if (product.category_id) return product.category_id;
  return '';
}

export function getSupplierId(product: Partial<ProductListItem>): string {
  // API returns supplier as UUID string directly (not nested object)
  if (typeof product.supplier === 'string' && product.supplier) return product.supplier;
  if (product.supplier_id) return product.supplier_id;
  return '';
}

// ── form helpers ───────────────────────────────────────────────────────────────

import { getImageUrl } from '../../utils';

/** ISO datetime -> giá trị cho <input type="date"> (yyyy-MM-dd), rỗng nếu không hợp lệ. */
function toDateInputValue(value: unknown): string {
  const s = typeof value === 'string' ? value : '';
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
}

export function toProductFormData(product?: Partial<ProductListItem>): ProductFormData {
  if (!product) return emptyProductForm;

  const raw = asRecord(product);
  const imageUrls = Array.isArray(raw?.image_urls) ? (raw.image_urls as unknown[]) : [];
  const firstImage = imageUrls.length > 0 ? asString(imageUrls[0]) : undefined;

  // Nạp lại store_prices từ chi tiết sản phẩm — trước đây luôn gán [] nên giá
  // theo cửa hàng đã lưu không hiển thị khi mở lại modal edit (bug Store Prices).
  // BE có thể trả `store` (dạng write) hoặc `store_id` (dạng read) nên nhận cả hai.
  const rawStorePrices = Array.isArray(raw?.store_prices) ? (raw.store_prices as unknown[]) : [];
  const storePrices = rawStorePrices
    .map((sp) => {
      const r = asRecord(sp) ?? {};
      return {
        store: asString(r.store) ?? asString(r.store_id) ?? '',
        price: r.price != null ? String(r.price) : '',
        valid_from: toDateInputValue(r.valid_from),
        valid_to: toDateInputValue(r.valid_to),
      };
    })
    .filter((sp) => sp.store && sp.price);

  // API uses "price" for selling price
  const priceRaw = product.price ?? product.base_price;
  const price = Number.parseFloat(String(priceRaw ?? '0'));
  const costPrice = Number.parseFloat(String(product.cost_price ?? '0'));
  const compareAtPrice = Number.parseFloat(String(product.compare_at_price ?? '0'));

  return {
    sku: product.sku ?? '',
    name: product.name ?? '',
    barcode: product.barcode ?? '',
    categoryId: getCategoryId(product),
    supplierId: getSupplierId(product),
    brand: product.brand ?? '',
    unit: product.unit ?? 'piece',
    basePrice: Number.isFinite(price) && price > 0 ? String(price) : '',
    compareAtPrice: Number.isFinite(compareAtPrice) && compareAtPrice > 0 ? String(compareAtPrice) : '',
    costPrice: Number.isFinite(costPrice) && costPrice > 0 ? String(costPrice) : '',
    // `null` = kế thừa mức của cửa hàng -> để ô trống, KHÔNG điền số thay
    taxRate: product.tax_rate ?? '',
    status: product.status ?? 'ACTIVE',
    shortDescription: product.short_description ?? '',
    description: product.description ?? '',
    tags: Array.isArray(product.tags) ? (product.tags as string[]).join(', ') : '',
    stockTrackingEnabled: product.stock_tracking_enabled ?? false,
    imageUrl: getImageUrl(product.image_url ?? firstImage ?? ''),

    isAgeRestricted: product.is_age_restricted ?? false,
    ageRestrictionType: product.age_restriction_type ?? '',
    minAge: product.min_age != null ? String(product.min_age) : '',
    isBeer: product.is_beer ?? false,
    // `null` = chưa khai nồng độ. Giữ nguyên chuỗi rỗng, TUYỆT ĐỐI không đổi
    // thành '0' — 0% là một lời khai (nước ngọt), còn rỗng là chưa khai gì.
    alcoholByVolume: product.alcohol_by_volume != null ? String(product.alcohol_by_volume) : '',

    storePrices: storePrices,
  };
}

/**
 * Nồng độ cồn có bắt buộc phải khai không.
 *
 * Đúng luật PL: rượu KHÔNG phải bia thì phải khai nồng độ. Bia được miễn trừ
 * bất kể mạnh bao nhiêu, nên `isBeer` phải được hỏi riêng chứ không suy ra từ
 * con số nồng độ.
 */
export function requiresAlcoholStrength(form: ProductFormData): boolean {
  return form.isAgeRestricted && form.ageRestrictionType === 'ALCOHOL' && !form.isBeer;
}

/**
 * Người dùng có động vào nhóm field 18+ hay không.
 *
 * `original` là form lúc mới mở modal edit. Không có `original` (tạo mới) thì
 * coi như có động vào — sản phẩm mới luôn phải khai đủ.
 */
export function hasTouchedRestrictionFields(
  form: ProductFormData,
  original?: ProductFormData
): boolean {
  if (!original) return true;
  return restrictionTriggerFields.some((field) => {
    const a = form[field];
    const b = original[field];
    return typeof a === 'string' && typeof b === 'string'
      ? a.trim() !== b.trim()
      : a !== b;
  });
}

/** Nồng độ về đúng dạng backend chờ: 1 chữ số thập phân ('5.2'). */
export function normalizeAbv(value: string): string {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n.toFixed(1) : '';
}

export function normalizePrice(value: string): string {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return '0.00';
  return n.toFixed(2);
}

/**
 * Bug #209: mọi thông báo validation ở đây từng là chuỗi tiếng Anh viết cứng,
 * nên đổi giao diện sang PL/UA rồi bấm Save là nhãn trường đã dịch mà lỗi bên
 * dưới vẫn tiếng Anh. `t` đứng TRƯỚC `original` vì `original` là tham số tuỳ
 * chọn — thêm vào sau sẽ khiến call site nào bỏ qua `original` không truyền
 * được `t`.
 */
export function validateProductField(
  field: keyof ProductFormData,
  form: ProductFormData,
  t: (key: string) => string,
  original?: ProductFormData
): string | undefined {
  if (field === 'sku' && !form.sku.trim()) return t('validationSkuRequired');
  if (field === 'name' && !form.name.trim()) return t('validationProductNameRequired');
  if (field === 'unit' && !form.unit.trim()) return t('validationUnitRequired');
  if (field === 'basePrice') {
    if (!form.basePrice.trim()) return t('validationSellingPriceRequired');
    const p = Number.parseFloat(form.basePrice);
    if (!Number.isFinite(p) || p <= 0) return t('validationSellingPriceGreaterThanZero');
  }
  if (field === 'compareAtPrice' && form.compareAtPrice.trim()) {
    const cap = Number.parseFloat(form.compareAtPrice);
    const price = Number.parseFloat(form.basePrice);
    if (!Number.isFinite(cap) || cap <= 0) return t('validationCompareAtPriceGreaterThanZero');
    if (Number.isFinite(price) && cap < price) return t('validationCompareAtPriceAtLeastSellingPrice');
  }

  if (field === 'ageRestrictionType' && form.isAgeRestricted && !form.ageRestrictionType) {
    return t('validationAgeRestrictionTypeRequired');
  }

  if (field === 'minAge' && form.minAge.trim()) {
    const age = Number.parseInt(form.minAge, 10);
    if (!Number.isFinite(age) || age < 1 || age > 120) return t('validationMinAgeRange');
  }

  if (field === 'alcoholByVolume') {
    const raw = form.alcoholByVolume.trim();

    // Chỉ đòi khai nồng độ khi người dùng thật sự động vào nhóm field 18+.
    // Sản phẩm rượu cũ chưa khai nồng độ vẫn phải sửa được giá và tên — chặn ở
    // đây là biến một field mới thành hàng rào trước mọi thao tác thường ngày,
    // đúng ca backend đã nói rõ là KHÔNG chặn.
    if (!raw && requiresAlcoholStrength(form) && hasTouchedRestrictionFields(form, original)) {
      return t('validationAlcoholStrengthRequired');
    }

    if (raw) {
      const abv = Number.parseFloat(raw);
      if (!Number.isFinite(abv) || abv < 0 || abv > 100) return t('validationAbvRange');
    }
  }

  return undefined;
}

export function validateProductForm(
  form: ProductFormData,
  t: (key: string) => string,
  original?: ProductFormData
): ProductFormErrors {
  const errors: ProductFormErrors = {};
  for (const field of formFieldOrder) {
    const msg = validateProductField(field, form, t, original);
    if (msg) errors[field] = msg;
  }
  return errors;
}

export function toCreatePayload(form: ProductFormData): CreateProductRequest {
  const imageUrl = form.imageUrl.trim();
  const isBlob = imageUrl.startsWith('blob:');
  const imageUrls = imageUrl && !isBlob ? [imageUrl] : undefined;

  const tags = form.tags.trim()
    ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
    : undefined;

  return {
    sku: form.sku.trim(),
    barcode: form.barcode.trim() || undefined,
    name: form.name.trim(),
    // Tạo mới: ô thuế trống = bỏ hẳn key, BE tự kế thừa mức của cửa hàng
    tax_rate: form.taxRate.trim() || undefined,
    short_description: form.shortDescription.trim() || undefined,
    category: form.categoryId || undefined,
    supplier: form.supplierId || undefined,
    brand: form.brand.trim() || undefined,
    unit: form.unit.trim(),
    price: normalizePrice(form.basePrice),
    compare_at_price: form.compareAtPrice.trim() ? normalizePrice(form.compareAtPrice) : undefined,
    cost_price: form.costPrice.trim() ? normalizePrice(form.costPrice) : undefined,
    status: form.status,
    tags,
    stock_tracking_enabled: form.stockTrackingEnabled,
    // KHÔNG gửi `min_stock_threshold` nữa (09/09/2026): ngưỡng cảnh báo do BE
    // tính = 20% mốc tồn sau lần nhập gần nhất. Số nhập tay không còn chỗ dùng.
    image_urls: imageUrls,

    is_age_restricted: form.isAgeRestricted,
    // Chỉ gửi phân loại khi sản phẩm thật sự bị hạn chế — gửi kèm lúc
    // `is_age_restricted = false` là bật validate nồng độ cho một món không hề
    // là rượu.
    age_restriction_type: form.isAgeRestricted ? form.ageRestrictionType || undefined : undefined,
    min_age: form.isAgeRestricted && form.minAge.trim() ? Number(form.minAge) : undefined,
    is_beer: form.isAgeRestricted && form.ageRestrictionType === 'ALCOHOL' ? form.isBeer : undefined,
    alcohol_by_volume: form.alcoholByVolume.trim()
      ? normalizeAbv(form.alcoholByVolume)
      : undefined,

    store_prices: form.storePrices.length > 0
      ? form.storePrices
          .filter((sp) => sp.store && sp.price)
          .map((sp) => ({
            store: sp.store,
            price: normalizePrice(sp.price),
            ...(sp.valid_from ? { valid_from: new Date(sp.valid_from).toISOString() } : {}),
            ...(sp.valid_to ? { valid_to: new Date(sp.valid_to).toISOString() } : {}),
          }))
      : undefined,
  } as unknown as CreateProductRequest;
}

export function toUpdatePayload(
  form: ProductFormData,
  /** Form lúc mới mở modal — dùng để biết nhóm field 18+ có bị đụng vào không. */
  original?: ProductFormData
): UpdateProductRequest {
  const imageUrl = form.imageUrl.trim();
  const isBlob = imageUrl.startsWith('blob:');
  // Bug #45: khi user Remove ảnh (imageUrl rỗng) phải gửi [] để server XOÁ ảnh.
  // Trước đây gửi undefined = "không đổi" nên ảnh vẫn còn sau khi Save.
  // blob: = ảnh đang upload dở/lỗi — không đổi ảnh trên server.
  const imageUrls = imageUrl ? (isBlob ? undefined : [imageUrl]) : [];

  const tags = form.tags.trim()
    ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
    : undefined;

  /**
   * Backend chỉ bật validate nồng độ khi request ĐỘNG VÀO `alcohol_by_volume`,
   * `is_beer` hoặc `age_restriction_type`. Gửi cả ba ở mọi lần lưu là tự bật
   * validate lên đầu mình: đổi giá một chai vodka cũ chưa khai nồng độ sẽ ăn
   * 400 dù người dùng không hề đụng tới thông tin rượu.
   */
  const restrictionTouched = hasTouchedRestrictionFields(form, original);
  const restrictionPayload = restrictionTouched
    ? {
        is_age_restricted: form.isAgeRestricted,
        age_restriction_type: form.isAgeRestricted
          ? form.ageRestrictionType || null
          : null,
        is_beer: form.isAgeRestricted && form.ageRestrictionType === 'ALCOHOL'
          ? form.isBeer
          : false,
        // Rỗng -> `null` (xoá lời khai), KHÔNG phải `undefined` (giữ nguyên).
        // Bỏ tích "hàng 18+" mà để lại nồng độ cũ là để dữ liệu tự mâu thuẫn.
        alcohol_by_volume: form.alcoholByVolume.trim()
          ? normalizeAbv(form.alcoholByVolume)
          : null,
      }
    : {};

  // `min_age` KHÔNG nằm trong nhóm kích hoạt validate nên gửi độc lập được.
  const minAgeChanged = !original || original.minAge.trim() !== form.minAge.trim();
  const minAgePayload = minAgeChanged
    ? { min_age: form.minAge.trim() ? Number(form.minAge) : null }
    : {};

  return {
    ...restrictionPayload,
    ...minAgePayload,
    name: form.name.trim(),
    // Sửa: ô thuế trống LÀ ý định "quay lại dùng mức của cửa hàng" -> gửi
    // `null` tường minh. Gửi `undefined` = giữ nguyên mức cũ, tức người dùng
    // xoá ô mà không có gì xảy ra (đúng lỗi của ảnh ở bug #45).
    tax_rate: form.taxRate.trim() || null,
    barcode: form.barcode.trim() || undefined,
    short_description: form.shortDescription.trim() || undefined,
    category: form.categoryId || undefined,
    supplier: form.supplierId || undefined,
    brand: form.brand.trim() || undefined,
    unit: form.unit.trim(),
    price: normalizePrice(form.basePrice),
    compare_at_price: form.compareAtPrice.trim() ? normalizePrice(form.compareAtPrice) : undefined,
    cost_price: form.costPrice.trim() ? normalizePrice(form.costPrice) : undefined,
    status: form.status,
    tags,
    stock_tracking_enabled: form.stockTrackingEnabled,
    // KHÔNG gửi `min_stock_threshold` nữa (09/09/2026): ngưỡng cảnh báo do BE
    // tính = 20% mốc tồn sau lần nhập gần nhất. Số nhập tay không còn chỗ dùng.
    image_urls: imageUrls,
    // Trước đây update KHÔNG gửi store_prices (chỉ create mới gửi), nên giá theo
    // cửa hàng thêm ở modal Edit không bao giờ tới server. Gửi mảng rỗng khi user
    // xoá hết để server biết là "xoá", thay vì undefined = "không đổi".
    store_prices: form.storePrices
      .filter((sp) => sp.store && sp.price)
      .map((sp) => ({
        store: sp.store,
        price: normalizePrice(sp.price),
        ...(sp.valid_from ? { valid_from: new Date(sp.valid_from).toISOString() } : {}),
        ...(sp.valid_to ? { valid_to: new Date(sp.valid_to).toISOString() } : {}),
      })),
  } as unknown as UpdateProductRequest;
}

/**
 * Nhãn 18+ ngắn gọn cho dòng trong bảng sản phẩm.
 *
 * Trả `undefined` khi sản phẩm không bị hạn chế. `missingAbv` bật khi là rượu,
 * không phải bia, và chưa khai nồng độ — ca này backend coi là quá mạnh và sẽ
 * chặn bán, nên phải nhìn thấy được từ danh sách.
 */
function buildRestrictionBadge(product: ProductListItem): {
  restrictionLabel?: string;
  missingAbv?: boolean;
} {
  if (!product.is_age_restricted) return {};

  const type = product.age_restriction_type ?? 'OTHER';
  if (type !== 'ALCOHOL') return { restrictionLabel: type };

  if (product.is_beer) return { restrictionLabel: 'ALCOHOL · bia' };

  const abv = product.alcohol_by_volume;
  // Rỗng KHÔNG phải 0% — là "chưa khai". Hai chuyện khác hẳn nhau.
  if (abv == null || String(abv).trim() === '') {
    return { restrictionLabel: 'ALCOHOL · ?%', missingAbv: true };
  }

  return { restrictionLabel: `ALCOHOL · ${abv}%` };
}

export function mapProductToItem(product: ProductListItem): ProductItem {
  const basePrice = toPriceNumber(product);
  const raw = asRecord(product);
  const imageUrls = Array.isArray(raw?.image_urls) ? raw.image_urls : [];
  const firstImageUrl = imageUrls.length > 0 ? asString(imageUrls[0]) : undefined;
  // cost_price may be string or number from API
  const costRaw = Number.parseFloat(String(product.cost_price ?? raw?.cost_price ?? '0'));
  const costPrice = Number.isFinite(costRaw) && costRaw > 0 ? costRaw : 0;

  return {
    id: product.id,
    imageUrl: getImageUrl(product.image_url ?? firstImageUrl),
    productCode: product.sku,
    barcode: product.barcode ?? '',
    name: product.name,
    categoryPath: getCategoryName(product),
    price: basePrice,
    costPrice,
    // Bug #52: `vatRate: 10` đã gỡ. Đây là hằng số bịa — API sản phẩm không trả
    // trường thuế nào, nên mọi dòng đều hiện "10%" bất kể sản phẩm gì.
    //
    // Cũng không map tồn kho nữa (09/09/2026): cột Stock đã gỡ khỏi bảng sản
    // phẩm, tồn chỉ hiện ở trang Inventory — nơi có bộ chọn cửa hàng, nên con số
    // mới nói rõ được là tồn của cửa hàng nào.
    status: product.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE',
    ...buildRestrictionBadge(product),
  };
}
